// views/Dashboard/PainelExecutivo/sections/comercial/ListaNegociacoesModal.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Download, Loader2, ExternalLink, AlertTriangle, CalendarClock } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { LinhaNegociacao } from '../../../../../src/lib/dashboard/negociacoesDaEtapa';
import { ROTULOS_CANAL, rotuloMes } from '../../../../../src/lib/dashboard/negociacoesDaEtapa';
import { STATUS_LABELS } from '../../../../../src/lib/crmVisuals';
import { propsFundoModal } from '../../../../../src/lib/backdropModal';

interface Props {
  aberto: boolean;
  etapa: string;
  /** Explica o que entrou na lista — a etapa conta pela data DELA. */
  descricao: string;
  linhas: LinhaNegociacao[];
  exportando: boolean;
  onFechar: () => void;
  onExportar: (visiveis: LinhaNegociacao[]) => void;
}

const TAMANHO_PAGINA = 100;

const fmtKwh = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`;
const fmtData = (v: string | null) => {
  if (!v) return '—';
  const [a, m, d] = v.slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : '—';
};
const normalizar = (t: string | null | undefined) => (t || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();

/**
 * As negociações que formam um card do funil.
 *
 * Mesmo desenho da lista de boletos do Financeiro (modal largo, busca, Excel),
 * porque é o mesmo gesto: clicar no número para ver de onde ele vem. Aqui o que
 * costuma explicar o card é o kWh concentrado em poucas negociações — por isso
 * a lista já chega ordenada por consumo.
 */
const ListaNegociacoesModal: React.FC<Props> = ({ aberto, etapa, descricao, linhas, exportando, onFechar, onExportar }) => {
  const [busca, setBusca] = useState('');
  const [limite, setLimite] = useState(TAMANHO_PAGINA);
  const buscaRef = useRef<HTMLInputElement>(null);
  const focoAnterior = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setBusca('');
    setLimite(TAMANHO_PAGINA);
  }, [aberto, etapa]);

  useEffect(() => {
    if (aberto) {
      focoAnterior.current = document.activeElement as HTMLElement | null;
      const t = setTimeout(() => buscaRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    focoAnterior.current?.focus();
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberto, onFechar]);

  const visiveis = useMemo(() => {
    const termo = normalizar(busca.trim());
    const digitos = busca.replace(/\D/g, '');
    if (!termo) return linhas;
    return linhas.filter(
      (l) =>
        normalizar(l.cliente).includes(termo)
        || normalizar(l.responsavel).includes(termo)
        || normalizar(l.cidade).includes(termo)
        || (digitos.length >= 3 && (l.cpfCnpj || '').replace(/\D/g, '').includes(digitos)),
    );
  }, [linhas, busca]);

  if (!aberto) return null;

  const kwhTotal = linhas.reduce((s, l) => s + l.kwh, 0);
  const kwhVisiveis = visiveis.length === linhas.length ? kwhTotal : visiveis.reduce((s, l) => s + l.kwh, 0);

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-6 animate-fade-in" {...propsFundoModal(onFechar)}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="lista-negociacoes-titulo"
        className="w-full max-w-[1800px] h-[92vh] max-h-full rounded-2xl overflow-hidden bg-white dark:bg-gray-900 shadow-2xl flex flex-col"
      >
        <header className="px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 id="lista-negociacoes-titulo" className="text-lg font-poppins font-bold text-gray-900 dark:text-white">
                {etapa}
              </h2>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mt-0.5">
                {linhas.length.toLocaleString('pt-BR')} negociações · {fmtKwh(kwhTotal)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{descricao}</p>
              {/* O title do "(aprox.)" só aparece no mouse: a explicação fica escrita aqui também. */}
              {linhas.some((l) => l.dataAproximada) && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">(aprox.) = reprovação anterior a 19/10/2026, com a data reconstruída pela decisão da análise; pode não ser o dia exato.</p>
              )}
            </div>
            <button type="button" onClick={onFechar} aria-label="Fechar lista" className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800">
              <X size={18} />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4">
            <div className="relative flex-1 sm:max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={buscaRef}
                type="text"
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setLimite(TAMANHO_PAGINA); }}
                placeholder="Buscar cliente, responsável, cidade ou CPF/CNPJ..."
                aria-label="Buscar na lista de negociações"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <button
              type="button"
              onClick={() => onExportar(visiveis)}
              disabled={exportando || visiveis.length === 0}
              className="sm:ml-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-50 transition-colors"
            >
              {exportando ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              Exportar esta lista
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          {visiveis.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-16">
              {linhas.length === 0 ? 'Nenhuma negociação nesta etapa, com os filtros atuais.' : 'Nenhuma negociação encontrada para esta busca.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-xs border-b border-gray-100 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Cliente</th>
                  <th className="px-3 py-2 text-left font-medium">Responsável</th>
                  <th className="px-3 py-2 text-left font-medium">Canal</th>
                  <th className="px-3 py-2 text-left font-medium">Data da etapa</th>
                  <th className="px-3 py-2 text-right font-medium">Consumo</th>
                  <th className="px-3 py-2 text-left font-medium">Status atual</th>
                  <th className="px-3 py-2 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {visiveis.slice(0, limite).map((l) => (
                  <tr key={`${l.id}-${l.data}`} className="hover:bg-gray-50/70 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2.5 max-w-[320px]">
                      <p className="font-medium text-gray-800 dark:text-gray-100 truncate" title={l.cliente}>{l.cliente}</p>
                      <p className="text-[11px] text-gray-400 truncate">
                        {[l.cpfCnpj, l.cidade, l.tipoCliente, l.origem].filter(Boolean).join(' · ') || '—'}
                      </p>
                      {/* Chegou nesta etapa sem registrar as anteriores: é o que
                          explica o funil não ser decrescente e o kWh "aparecer do
                          nada" numa etapa do fim. */}
                      {/* Começou num mês e chegou nesta etapa em outro: é o que
                          explica aparecer em "Termo Assinado" de setembro sem
                          estar em "Termo Enviado" do mesmo mês. */}
                      {l.atravessouMes && l.etapaAnterior && (
                        <p
                          className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600 dark:text-sky-400"
                          title={`${l.etapaAnterior.label} aconteceu em ${fmtData(l.etapaAnterior.data)}, em outro mês — a negociação atravessou a virada.`}
                        >
                          <CalendarClock size={11} />
                          {l.etapaAnterior.label.toLowerCase()} em {rotuloMes(l.etapaAnterior.data)}
                        </p>
                      )}
                      {l.etapasPuladas.length > 0 && (
                        <p
                          className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400"
                          title={`Esta negociação não tem registro de ${l.etapasPuladas.join(', ')} — chegou nesta etapa sem passar por elas.`}
                        >
                          <AlertTriangle size={11} />
                          pulou: {l.etapasPuladas.join(', ').toLowerCase()}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {l.responsavel ?? <span className="text-amber-600 dark:text-amber-400" title="Sem responsável no cadastro — o canal cai em Comercial por padrão">Sem responsável</span>}
                    </td>
                    <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">{ROTULOS_CANAL[l.canal]}</td>
                    <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {fmtData(l.data)}
                      {l.dataAproximada && (
                        <span className="ml-1 text-[11px] text-gray-400 dark:text-gray-500" title="Reprovação anterior a 19/10/2026: a data foi reconstruída pela decisão da análise e pode não ser o dia exato">(aprox.)<span className="sr-only">: data reconstruída, pode não ser o dia exato</span></span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-gray-900 dark:text-white whitespace-nowrap">{fmtKwh(l.kwh)}</td>
                    <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {STATUS_LABELS[l.status] || l.status || '—'}
                      {l.motivoReprovacao && (
                        <span className="block max-w-[16rem] whitespace-normal text-[11px] font-semibold leading-tight text-red-600 dark:text-red-400">Motivo: {l.motivoReprovacao}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end">
                        {/* Abre a negociação no CRM, em nova aba: quem está lendo o
                            painel não perde o recorte que estava analisando. */}
                        <Link
                          // É assim que o CRM abre uma negociação (ver DetailFeed.tsx):
                          // por query string, não por rota própria.
                          to={`/crm?negociacaoId=${l.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary/50 hover:text-primary whitespace-nowrap"
                        >
                          <ExternalLink size={13} />
                          Abrir
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* md:pl-24: o botão flutuante da Vick (fixed bottom-5 left-5, 56px, só a
            partir de md) cai exatamente em cima do contador e do "Carregar mais",
            que ficam à esquerda deste rodapé. 96px livra o botão com folga. Abaixo
            de md o botão não aparece, então o padding original fica (5840). */}
        <footer className="px-5 md:pl-24 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            Mostrando {Math.min(limite, visiveis.length).toLocaleString('pt-BR')} de {visiveis.length.toLocaleString('pt-BR')}
          </span>
          {visiveis.length > limite && (
            <button type="button" onClick={() => setLimite((l) => l + TAMANHO_PAGINA)} className="text-primary font-semibold hover:underline sm:ml-2 text-left">
              Carregar mais {Math.min(TAMANHO_PAGINA, visiveis.length - limite)}
            </button>
          )}
          <span className="sm:ml-auto font-bold text-gray-900 dark:text-white">
            {visiveis.length === linhas.length ? 'Total' : 'Total da busca'}: {fmtKwh(kwhVisiveis)}
          </span>
        </footer>
      </aside>
    </div>,
    document.body,
  );
};

export default ListaNegociacoesModal;
